import axios from "axios";
import { encodeData } from "../common/encodeData";
import { getRequestedCountForBulk } from "./bulkPlacementBidUpdate";

export { getRequestedCountForBulk };

export const NEGATIVE_MATCH_TYPE = {
  EXACT: "negativeExact",
  PHRASE: "negativePhrase",
  EMPTY: "",
};

export const NEGATIVE_LEVEL = {
  RESPECTIVE: "respective",
  ACCOUNT: "account",
  CAMPAIGN: "campaign",
  ADGROUP: "adgroup",
};

export function mapMatchTypeToAdjustmentType(matchType, { forceExact = false } = {}) {
  if (forceExact) return NEGATIVE_MATCH_TYPE.EXACT;
  if (matchType === "Phrase" || matchType === "phrase" || matchType === "negativePhrase") {
    return NEGATIVE_MATCH_TYPE.PHRASE;
  }
  if (matchType === "Exact" || matchType === "exact" || matchType === "negativeExact") {
    return NEGATIVE_MATCH_TYPE.EXACT;
  }
  if (matchType === "" || matchType == null) return NEGATIVE_MATCH_TYPE.EXACT;
  return String(matchType);
}

/**
 * Fetch campaigns for negative modal dropdowns (includes platform_id).
 */
export async function fetchCampaignsForNegativeModal({
  clientId,
  searchTerm = "",
  limit = 200,
}) {
  const params = [
    "source=amazon",
    "fields=id,name,platform_id,status",
    `limit=${limit}`,
    "order_by=daily_budget",
    "sorting_order=DESC",
  ];
  const term = String(searchTerm || "").trim();
  if (term) {
    params.push(
      `filtering=${encodeData([
        { field_name: "name", operator: "CONTAINS", value: term },
      ])}`
    );
  }

  const url = `${process.env.REACT_APP_ADS_API}/client/${clientId}/campaigns?${params.join("&")}`;
  const response = await axios.get(url);
  return (response?.data?.data || [])
    .map((item) => ({
      id: item?.id,
      name: item?.name || "",
      platform_id: item?.platform_id != null ? String(item.platform_id) : "",
      status: item?.status,
      label: item?.name || "",
      value: item?.platform_id != null ? String(item.platform_id) : String(item?.id || ""),
    }))
    .filter((item) => item.platform_id || item.id);
}

/**
 * Fetch adgroups for selected campaigns.
 * Sends filtering: campaign_id IN [selected campaign ids] (+ optional name CONTAINS).
 */
export async function fetchAdgroupsForNegativeModal({
  clientId,
  campaignAlphaIds = [],
  searchTerm = "",
  limit = 500,
}) {
  const params = [
    "source=amazon",
    "fields=id,name,platform_id,campaign_id,status",
    `limit=${limit}`,
  ];

  const filtering = [];
  const selectedCampaignIds = (campaignAlphaIds || []).map(String).filter(Boolean);
  if (selectedCampaignIds.length) {
    filtering.push({
      field_name: "campaign_id",
      operator: "IN",
      values: selectedCampaignIds,
    });
  }
  const term = String(searchTerm || "").trim();
  if (term) {
    filtering.push({ field_name: "name", operator: "CONTAINS", value: term });
  }
  if (filtering.length) {
    params.push(`filtering=${encodeData(filtering)}`);
  }

  const url = `${process.env.REACT_APP_ADS_API}/client/${clientId}/adsets?${params.join("&")}`;
  const response = await axios.get(url);

  return (response?.data?.data || [])
    .map((item) => ({
      id: item?.id,
      name: item?.name || "",
      platform_id: item?.platform_id != null ? String(item.platform_id) : "",
      campaign_id: item?.campaign_id != null ? String(item.campaign_id) : "",
      status: item?.status,
      label: item?.name || "",
      value: item?.platform_id != null ? String(item.platform_id) : String(item?.id || ""),
    }))
    .filter((item) => item.platform_id || item.id);
}

export function buildBulkNegativeTargetBody({
  level,
  query,
  email,
  requestedCount,
  adjustmentType,
  campaignPlatformIds = [],
  adsetPlatformIds = [],
}) {
  const body = {
    filters: {
      query: { ...(query || {}) },
    },
    operation: {
      adjustmentType:
        adjustmentType === undefined || adjustmentType === null || adjustmentType === ""
          ? NEGATIVE_MATCH_TYPE.EXACT
          : adjustmentType,
    },
    email: email || "",
    requestedCount: Number(requestedCount) || 0,
  };

  if (level === NEGATIVE_LEVEL.CAMPAIGN) {
    body.campaign_ids = (campaignPlatformIds || []).map(String);
  }
  if (level === NEGATIVE_LEVEL.ADGROUP) {
    body.adset_ids = (adsetPlatformIds || []).map(String);
  }

  return body;
}

export async function submitBulkNegativeTargetUpdate({
  clientId,
  level,
  body,
  token,
  skipEvents = true,
}) {
  const baseUrl = process.env.REACT_APP_ADS_API;
  const headers = {
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = token;

  let url;
  if (level === NEGATIVE_LEVEL.ACCOUNT) {
    url = `${baseUrl}/accounts/client/${clientId}:add-amazon-negative-target-on-account?type=amazon_seller&skip_events=${
      skipEvents ? "true" : "false"
    }`;
  } else {
    url = `${baseUrl}/client/${clientId}:add-amazon-negative-target-on-campaigns?source=amazon&skip_events=${
      skipEvents ? "true" : "false"
    }`;
  }

  const response = await axios.post(url, body, { headers });
  return response?.data;
}
